import { DateRangeViewEvent, DateRangeViewEventWorkArea, Timesheet, VisitWorkareaStatusRecord } from "./scheduleApi"
import { VisitWorkareaStatuses } from "../../common/constants"

export type StatusItem = {
  name: string
  workareaId: number
  status: number | null
  hasTimesheets: boolean
  manhoursDisplay: string | null
}

const STATUS_PRIORITY: Record<number, number> = {
  [VisitWorkareaStatuses.Completed]: 0,
  [VisitWorkareaStatuses.CompletedUnbillable]: 1,
  [VisitWorkareaStatuses.InProgress]: 2,
  [VisitWorkareaStatuses.Skipped]: 3,
  [VisitWorkareaStatuses.SkippedReschedule]: 4,
}

export const formatManhours = (totalHours: number): string => {
  if (totalHours <= 0) {
    return '0 man hours'
  }
  const rounded = Math.round(totalHours * 10) / 10
  return `${rounded} man hour${rounded === 1 ? '' : 's'}`
}

export const buildStatusItems = (
  configuredWorkAreas: DateRangeViewEventWorkArea[],
  allWorkAreas: DateRangeViewEventWorkArea[],
  workareaStatuses?: VisitWorkareaStatusRecord[],
  timesheets?: Timesheet[],
): StatusItem[] => {
  const workareaMap = new Map<number, DateRangeViewEventWorkArea>(
    allWorkAreas.map(wa => [wa.id, wa])
  )
  const timesheetWorkareaIds = new Set(
    (timesheets ?? []).filter(t => t.projectWorkareaId).map(t => t.projectWorkareaId!)
  )
  const manhoursByWorkarea = new Map<number, number>()
  for (const t of timesheets ?? []) {
    if (t.projectWorkareaId) {
      manhoursByWorkarea.set(t.projectWorkareaId, (manhoursByWorkarea.get(t.projectWorkareaId) ?? 0) + t.totalTime)
    }
  }

  const configuredIds = new Set(configuredWorkAreas.map(wa => wa.id))
  const displayWorkareaIds = new Set(configuredIds)
  for (const id of timesheetWorkareaIds) {
    displayWorkareaIds.add(id)
  }

  const seenKeys = new Set<string>()
  const seenWorkareaIds = new Set<number>()
  const items: StatusItem[] = []

  if (workareaStatuses) {
    for (const s of workareaStatuses) {
      if (!displayWorkareaIds.has(s.workareaId)) {
        continue
      }
      const key = `${s.workareaId}-${s.status}`
      if (!seenKeys.has(key)) {
        seenKeys.add(key)
        seenWorkareaIds.add(s.workareaId)
        const wa = workareaMap.get(s.workareaId)
        const manhours = manhoursByWorkarea.get(s.workareaId)
        items.push({
          name: wa?.name ?? s.workareaName ?? `Work Area ${s.workareaId}`,
          workareaId: s.workareaId,
          status: s.status,
          hasTimesheets: timesheetWorkareaIds.has(s.workareaId),
          manhoursDisplay: manhours ? formatManhours(manhours) : null,
        })
      }
    }
  }

  for (const waId of displayWorkareaIds) {
    if (!seenWorkareaIds.has(waId)) {
      const wa = workareaMap.get(waId)
      const manhours = manhoursByWorkarea.get(waId)
      items.push({
        name: wa?.name ?? `Work Area ${waId}`,
        workareaId: waId,
        status: null,
        hasTimesheets: timesheetWorkareaIds.has(waId),
        manhoursDisplay: manhours !== undefined ? formatManhours(manhours) : null,
      })
    }
  }

  items.sort((a, b) => {
    const aPriority = a.status !== null ? (STATUS_PRIORITY[a.status] ?? 99) : 100
    const bPriority = b.status !== null ? (STATUS_PRIORITY[b.status] ?? 99) : 100
    return aPriority - bPriority
  })

  return items
}

export const formatAddress = (property: DateRangeViewEvent["property"]) => {
  if (!property) {
    return null
  }

  const parts: string[] = []

  if (property.address1) {
    parts.push(property.address1)
  }
  if (property.address2) {
    parts.push(property.address2)
  }

  const cityStateZip = [property.city, property.state].filter(Boolean).join(", ")
  if (cityStateZip || property.zip) {
    parts.push([cityStateZip, property.zip].filter(Boolean).join(" "))
  }

  return parts
}

export const formatWorkAreas = (workAreas: DateRangeViewEventWorkArea[]) => {
  if (workAreas.length === 0) {
    return null
  }

  if (workAreas.length === 1) {
    return workAreas[0].name
  }

  return `${workAreas[0].name} +${workAreas.length - 1} More`
}

export const formatEstimatedHours = (workAreas: DateRangeViewEventWorkArea[]) => {
  if (workAreas.length === 0) {
    return null
  }

  const totalHours = workAreas.reduce((sum, wa) => sum + (wa.estimatedHours || 0), 0)
  if (totalHours === 0) {
    return null
  }

  return `${totalHours} estimated man hour${totalHours !== 1 ? "s" : ""}`
}

export const shouldShowStatusSection = (
  statusItems: StatusItem[],
) => {
  return statusItems.length > 0
}
